
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { enhancedServiceData, EnhancedServiceData } from '@/data/enhancedServiceData';
import { serviceData } from '@/data/serviceDetailsData';
import ServiceHeader from '@/components/service/ServiceHeader';
import ServiceDescription from '@/components/service/ServiceDescription';
import ServiceProcess from '@/components/service/ServiceProcess';
import RelatedServices from '@/components/service/RelatedServices';
import ServiceCta from '@/components/service/ServiceCta';
import ServiceLocationLinks from '@/components/service/ServiceLocationLinks';
import ServiceFaqs from '@/components/service/ServiceFaqs';
import ServiceLocalContent from '@/components/service/ServiceLocalContent';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState<EnhancedServiceData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Check enhanced data first, then fall back to basic data
    if (serviceId && serviceId in enhancedServiceData) {
      setService(enhancedServiceData[serviceId]);
    } else if (serviceId && serviceId in serviceData) {
      // Convert basic service data to enhanced format with defaults
      const basicService = serviceData[serviceId];
      setService({
        ...basicService,
        slug: serviceId,
        metaTitle: `${basicService.title} Services | PK Landscape Associates`,
        metaDescription: basicService.description,
        heroHeading: basicService.title,
        localContent: {
          intro: '',
          regionalBenefits: [],
          seasonalTips: []
        },
        faqs: [],
        targetTowns: [],
        commercialAvailable: false
      } as EnhancedServiceData);
    } else {
      navigate('/services', { replace: true });
    }
    
    setLoading(false);
  }, [serviceId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse">Loading service details...</div>
      </div>
    );
  }

  if (!service) {
    return null;
  }

  // Generate schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.description,
    "provider": {
      "@type": "LandscapingBusiness",
      "name": "PK Landscape Associates",
      "url": "https://pklandscapect.com"
    },
    "areaServed": {
      "@type": "State",
      "name": "Connecticut"
    },
    "url": `https://pklandscapect.com/services/${service.slug || serviceId}`
  };

  const faqSchema = service.faqs && service.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": service.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{service.metaTitle || `${service.title} | PK Landscape Associates`}</title>
        <meta name="description" content={service.metaDescription || service.description} />
        <link rel="canonical" href={`https://pklandscapect.com/services/${service.slug || serviceId}`} />
        <meta property="og:title" content={service.metaTitle || service.title} />
        <meta property="og:description" content={service.metaDescription || service.description} />
        <meta property="og:url" content={`https://pklandscapect.com/services/${service.slug || serviceId}`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      <Navbar />
      
      <ServiceHeader service={service} />
      
      <div className="max-w-4xl mx-auto">
        <ServiceDescription service={service} />
        
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <ServiceProcess service={service} />
          </div>
        </div>
      </div>

      {/* Local Content Section */}
      {service.localContent && service.localContent.intro && (
        <ServiceLocalContent 
          localContent={service.localContent} 
          serviceName={service.title} 
        />
      )}

      {/* FAQ Section */}
      {service.faqs && service.faqs.length > 0 && (
        <ServiceFaqs 
          faqs={service.faqs} 
          serviceName={service.title} 
        />
      )}

      {/* Location Links */}
      {service.targetTowns && service.targetTowns.length > 0 && (
        <ServiceLocationLinks 
          targetTowns={service.targetTowns}
          serviceName={service.title}
          commercialAvailable={service.commercialAvailable}
        />
      )}

      <div className="max-w-4xl mx-auto">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <RelatedServices service={service} />
          </div>
        </div>
      </div>
      
      <ServiceCta serviceName={service.title} />
      
      <Footer />
    </div>
  );
};

export default ServiceDetail;
